import React, { memo, useCallback, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';

import { Box, Button, Popover, Typography } from '@mui/material';
import { ThemeProvider, useTheme } from '@mui/material/styles';
import { MagicWandIcon, SuccessIcon } from '@sharedCR/coreDesignSystem/icons';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { IconSize } from 'constants/coreDesignSystem';

import { PropertyAndLoanTestIds } from '../../../../../../PropertyAndLoan.testIds';
import { formatEnrichmentDisplay } from '../../enrichmentFieldMapping';

import {
  POPOVER_ANCHOR_ORIGIN,
  POPOVER_TRANSFORM_ORIGIN,
} from './EnrichmentFieldIndicators.constants';
import {
  applyButtonStyles,
  getMatchPopoverContentStyles,
  getPopoverContentStyles,
  getPopoverSlotProps,
  indicatorStyles,
  indicatorWrapperStyles,
  matchIndicatorStyles,
  popoverFooterStyles,
} from './EnrichmentFieldIndicators.styles';

const stopEventOnIcon = e => {
  e.stopPropagation();
};

/**
 * Click-based popover indicator. Uses MUI Popover instead of Tooltip because
 * Popover renders via its own Modal at document.body level with correct z-index,
 * while Tooltip's Popper gets trapped in the edit modal's stacking context.
 */
const FieldIndicator = memo(
  ({ currentValue, enrichmentValue, fieldName, onApply, originalValue, targetEl }) => {
    const theme = useTheme();
    const [anchorEl, setAnchorEl] = useState(null);

    const popoverSlotProps = useMemo(() => getPopoverSlotProps(theme), [theme]);
    const popoverContentStyles = useMemo(() => getPopoverContentStyles(theme), [theme]);
    const matchPopoverContentStyles = useMemo(() => getMatchPopoverContentStyles(theme), [theme]);
    const stringEnrichmentValue = _.isNil(enrichmentValue) ? '' : String(enrichmentValue);
    const stringCurrentValue = _.isNil(currentValue) ? '' : String(currentValue);
    const isMatch = stringCurrentValue === stringEnrichmentValue;
    const displayValue = formatEnrichmentDisplay(fieldName, enrichmentValue, originalValue);

    const onToggle = useCallback(e => {
      e.stopPropagation();
      setAnchorEl(prev => (prev ? null : e.currentTarget));
    }, []);

    const onClose = useCallback(() => {
      setAnchorEl(null);
    }, []);

    const onClickApply = useCallback(
      e => {
        e.stopPropagation();
        onApply(fieldName, enrichmentValue);
        setAnchorEl(null);
      },
      [onApply, fieldName, enrichmentValue],
    );

    if (!targetEl) {
      return null;
    }

    if (isMatch) {
      return createPortal(
        <ThemeProvider theme={theme}>
          <Box
            component="span"
            sx={indicatorWrapperStyles}
            onClick={stopEventOnIcon}
            onMouseDown={stopEventOnIcon}
          >
            <Box sx={matchIndicatorStyles} onClick={onToggle}>
              <SuccessIcon size={IconSize[20]} />
            </Box>
            <Popover
              anchorEl={anchorEl}
              anchorOrigin={POPOVER_ANCHOR_ORIGIN}
              open={!!anchorEl}
              slotProps={popoverSlotProps}
              transformOrigin={POPOVER_TRANSFORM_ORIGIN}
              onClose={onClose}
            >
              <Box sx={matchPopoverContentStyles}>
                <Typography color="success.dark" variant="bold12">
                  Verified Match
                </Typography>
                <Typography color="text.primary" variant="medium14">
                  {displayValue}
                </Typography>
                <Typography color="text.secondary" variant="regular12">
                  This value matches property records data.
                </Typography>
              </Box>
            </Popover>
          </Box>
        </ThemeProvider>,
        targetEl,
      );
    }

    return createPortal(
      <ThemeProvider theme={theme}>
        <Box
          component="span"
          sx={indicatorWrapperStyles}
          onClick={stopEventOnIcon}
          onMouseDown={stopEventOnIcon}
        >
          <Box sx={indicatorStyles} onClick={onToggle}>
            <MagicWandIcon size={IconSize[20]} />
          </Box>
          <Popover
            anchorEl={anchorEl}
            anchorOrigin={POPOVER_ANCHOR_ORIGIN}
            open={!!anchorEl}
            slotProps={popoverSlotProps}
            transformOrigin={POPOVER_TRANSFORM_ORIGIN}
            onClose={onClose}
          >
            <Box sx={popoverContentStyles}>
              <Typography color="info.dark" variant="bold12">
                Property Records
              </Typography>
              <Typography color="text.primary" variant="medium14">
                {displayValue}
              </Typography>
              <Box sx={popoverFooterStyles}>
                <Button
                  color="primary"
                  data-testid={PropertyAndLoanTestIds.ENRICHMENT_FIELD_INDICATOR_APPLY_BUTTON}
                  size="small"
                  sx={applyButtonStyles}
                  variant="contained"
                  onClick={onClickApply}
                >
                  Apply
                </Button>
              </Box>
            </Box>
          </Popover>
        </Box>
      </ThemeProvider>,
      targetEl,
    );
  },
);

FieldIndicator.displayName = 'FieldIndicator';

FieldIndicator.propTypes = {
  fieldName: PropTypes.string.isRequired,
  onApply: PropTypes.func.isRequired,
  currentValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  enrichmentValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  originalValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  targetEl: PropTypes.instanceOf(Element),
};

export default FieldIndicator;
